import { createFileRoute } from '@tanstack/react-router';
import { Shield, Lock, Eye, Key, FileCheck, AlertTriangle, Check } from "lucide-react";
import { ContentHeader, ContentSection, FeatureShowcase, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/features/security')({
  component: SecurityPage,
});

const securityFeatures = [
  {
    iconName: "Shield",
    title: "Zero-Trust Architecture",
    description: "Every request is authenticated and authorized. No implicit trust, ever.",
  },
  {
    iconName: "Lock",
    title: "Fine-Grained Policies",
    description: "Control access at the API, resource, and field level with powerful policy language.",
  },
  {
    iconName: "Eye",
    title: "Comprehensive Audit Logging",
    description: "Track every action with detailed, immutable audit logs for compliance.",
  },
  {
    iconName: "Key",
    title: "Secure Credential Management",
    description: "Encrypted secrets storage with automatic rotation and access policies.",
  },
  {
    iconName: "FileCheck",
    title: "Compliance Ready",
    description: "SOC2 Type II, GDPR, HIPAA compliance support out of the box.",
  },
  {
    iconName: "AlertTriangle",
    title: "Threat Detection",
    description: "Real-time monitoring for suspicious activity and automated alerts.",
  },
];

const accessControlFeatures = [
  "Role-Based Access Control (RBAC)",
  "Attribute-Based Access Control (ABAC)",
  "Field-level security policies",
  "Time-based access restrictions",
  "IP-based access control",
  "Device trust verification",
  "Multi-factor authentication",
  "Session management",
];

const complianceItems = [
  {
    title: "SOC2 Type II",
    description: "Annual audits verify our security controls meet the highest standards.",
  },
  {
    title: "GDPR Compliant",
    description: "Data processing agreements, right to deletion, and data portability.",
  },
  {
    title: "HIPAA Ready",
    description: "Business Associate Agreements available for healthcare data.",
  },
  {
    title: "Data Encryption",
    description: "AES-256 encryption at rest, TLS 1.3 in transit.",
  },
];

const faqItems = [
  {
    question: "What security certifications does Matricks have?",
    answer: "Matricks is SOC2 Type II certified, GDPR compliant, and supports HIPAA compliance with Business Associate Agreements. We undergo annual security audits and penetration testing.",
  },
  {
    question: "How are credentials and secrets stored?",
    answer: "All secrets are encrypted using AES-256 before storage. We integrate with enterprise secrets managers like HashiCorp Vault. Access to secrets is controlled by policies and logged.",
  },
  {
    question: "Can I implement custom access policies?",
    answer: "Yes, our policy engine supports complex rules based on user attributes, resource properties, time, location, and custom conditions. You can implement RBAC, ABAC, or hybrid models.",
  },
  {
    question: "How long are audit logs retained?",
    answer: "Audit logs are retained for 90 days by default, with options to extend to 1 year or longer for compliance requirements. Logs can be exported to your own SIEM.",
  },
];

function SecurityPage() {
  return (
    <div>
      <ContentHeader
        title="Enterprise Security"
        description="Built from the ground up with security in mind. Zero-trust architecture, fine-grained access control, and comprehensive compliance features for enterprise deployments."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Features", href: "/features" },
          { label: "Security" },
        ]}
        badge="Feature"
      />

      {/* Security Features */}
      <ContentSection
        id="features"
        title="Security Features"
        background="muted"
      >
        <FeatureShowcase features={securityFeatures} columns={3} variant="cards" />
      </ContentSection>

      {/* Access Control */}
      <ContentSection
        id="access-control"
        title="Access Control"
        description="Flexible, powerful access control for any requirement."
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h3 className="font-semibold text-lg mb-4">Capabilities</h3>
            <div className="space-y-3">
              {accessControlFeatures.map((feature) => (
                <div key={feature} className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl bg-muted/50 p-6">
            <h3 className="font-semibold text-lg mb-4">Example Policy</h3>
            <pre className="text-sm overflow-x-auto">
{`// Allow access to sales data only for
// sales team members during business hours
permit(
  principal in Group::"sales",
  action == "read",
  resource in ResourceType::"sales_data"
) when {
  context.time.hour >= 9 &&
  context.time.hour <= 17 &&
  principal.department == "sales"
};`}
            </pre>
          </div>
        </div>
      </ContentSection>

      {/* Compliance */}
      <ContentSection
        id="compliance"
        title="Compliance & Certifications"
        background="muted"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {complianceItems.map((item) => (
            <div key={item.title} className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold">{item.title}</h3>
              <p className="text-sm text-muted-foreground mt-2">{item.description}</p>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* Infrastructure */}
      <ContentSection
        id="infrastructure"
        title="Secure Infrastructure"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Network Security</h3>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li>- Private network isolation</li>
              <li>- DDoS protection</li>
              <li>- WAF (Web Application Firewall)</li>
              <li>- Regular penetration testing</li>
            </ul>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Data Security</h3>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li>- AES-256 encryption at rest</li>
              <li>- TLS 1.3 in transit</li>
              <li>- Key rotation</li>
              <li>- Data residency options</li>
            </ul>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Operational Security</h3>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li>- 24/7 monitoring</li>
              <li>- Incident response team</li>
              <li>- Regular security updates</li>
              <li>- Vulnerability management</li>
            </ul>
          </div>
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Enterprise-Grade Security"
        description="Protect your data and meet compliance requirements with Matricks' comprehensive security features."
        primaryAction={{ label: "Start Free Trial", href: "/login" }}
        secondaryAction={{ label: "Contact Sales", href: "/contact" }}
        background="primary"
      />
    </div>
  );
}
